import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { _post } from '../../../utils/network';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from '../../../components/Form/FormField/FormField.component';
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const activationToBoolean = value => value === 'active' ? true : value === 'deactive' ? false : null;

const BlogCategoriesList = () => {
	const { t } = useTranslation();
	const [blogCategories, setBlogCategories] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [searchParams, setSearchParams] = useState({
		title: null,
		is_active: null,
		sort: null,
		page: 1,
		count: 10
	});

	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('blog_categories'), link: '/blog-categories-list' },
		{ title: t('list') }
	];
	const activationsList = [
		{ key: t('active'), value: 'active' },
		{ key: t('deactive'), value: 'deactive' }
	];
	const sortsList = [
		{ key: t('newest'), value: 'date-desc' },
		{ key: t('oldest'), value: 'date-asc' }
	];

	const blogCategoriesListData = useMemo(() => ({
		head: [t('title'), t('status'), t('operation')],
		body: blogCategories.map(blogCategory => [
			blogCategory.title,
			<span className={`badge rounded-pill p-2 badge-subtle-${blogCategory.is_active ? 'success' : 'secondary'}`}>
				{blogCategory.is_active ? t('active') : t('deactive')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/blog-categories-edit/${blogCategory.id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['', '', 'ltr']
	}), [blogCategories, t]);

	const getBlogCategories = async () => {
		setSearchButtonIsLoading(true);
		const response = await _post('/blog_categories/admin/getBlogCategories', searchParams);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.blog_categories_count);
				setBlogCategories(response.data.blog_categories);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const filterBlogCategories = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return;
		const formData = new FormData(event.target);
		setSearchParams({
			...searchParams,
			title: formData.get('searchBlogCategoryTitle')?.trim() || null,
			is_active: activationToBoolean(formData.get('searchBlogCategoryIsActive')),
			sort: formData.get('searchBlogCategorySort') || null,
			page: 1
		});
	};

	useEffect(() => {
		getBlogCategories();
	}, [searchParams]);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('filter')} {t('blog_categories')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterBlogCategories}>
						<div className="row gx-2">
							<FormField fieldKey="searchBlogCategoryTitle" title={t('title')} isSearch />
							<FormField type="select" fieldKey="searchBlogCategoryIsActive" title={t('status')} selectItems={activationsList} isSearch />
							<FormField type="select" fieldKey="searchBlogCategorySort" title={t('sorting')} selectItems={sortsList} isSearch required />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="search" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('blog_categories')} pageNumber={searchParams.page} pageCounter={searchParams.count} totalCount={totalCount} data={blogCategoriesListData} />
			<Pagination pagesCount={Math.ceil(totalCount / searchParams.count)} action={page => setSearchParams({ ...searchParams, page })} />
		</>
	);
};

export default BlogCategoriesList;
